<template>
  <div class="base64-upload">
    <!-- 文件预览区域 -->
    <div v-if="previewUrl.length" class="preview-container">
      <div v-for="(item, index) in previewUrl" :key="index" class="preview-content">
        <!-- 图片预览 -->
        <img
          :src="showImages(item,`credentialImg${index}`)"
          :id="`credentialImg${index}`"
          class="preview-image"
          alt=""
          :style="{ 'max-width': imageWidth, 'max-height': imageHeight }"
        />
        <!-- 操作按钮 -->
        <div class="preview-actions" v-if="!disabled">
          <el-icon v-if="enableDownload" @click="handleDownload(item)" title="下载">
            <Download />
          </el-icon>
          <el-icon @click="handleReplace(item, index)" :disabled="disabled">
            <Upload />
          </el-icon>
          <el-icon @click="handleClear(index)" :disabled="disabled">
            <Delete />
          </el-icon>
        </div>
      </div>
    </div>
     <el-upload
      ref="uploadRef"
      v-bind="$attrs"
      :class="{'hide-upload': previewUrl.length >= limit }"
      :action="action"
      :headers="headers"
      :show-file-list="false"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-remove="handleRemove"
      :disabled="disabled"
      :before-upload="beforeUpload"
      :on-exceed="handleExceed"
      :limit="limit"
      multiple
    >
      <template #trigger>
        <slot name="trigger">
          <!-- <el-button :disabled="disabled" type="primary">选择文件</el-button> -->
            <el-icon class="avatar-uploader-icon"><Plus /></el-icon>
        </slot>
      </template>
    </el-upload>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import request from "@/utils/request.ts";
import { getToken, getTenant, getImageUrl } from "@/utils/auth";
import { ElMessage } from "element-plus";
import { Download } from "@element-plus/icons-vue";

const props = defineProps({
  // v-model 绑定值 (Base64字符串数组)
  modelValue: {
    type: Array,
    default: ""
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  // 接受的文件类型
  accept: {
    type: String,
    default: ""
  },
  // 图片最大宽度
  imageWidth: {
    type: String,
    default: "80px"
  },
  // 图片最大高度
  imageHeight: {
    type: String,
    default: "80px"
  },
  // 文件大小限制 (MB)
  maxSize: {
    type: Number,
    default: 10
  },
  // 上传地址 (保留el-upload的原生功能)
  action: {
    type: String,
    default: ""
  },
  // 上传文件数量限制
  limit: {
    type: Number,
    default: 5
  },
  fullImgUrl: {
    type: Boolean,
    default: true
  },
  isNotIncome: {
    type: Boolean,
    default: false
  },
  enableDownload: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "change", "error"]);

const uploadRef = ref(null);
const fileList = ref([]);

const headers = computed(() => ({
  Authorization: getToken(), // 从store获取token
  Tenant: getTenant(),
  "X-Requested-With": "XMLHttpRequest"
}));

const previewUrl = computed(() => {
  return props.modelValue ? props.modelValue.split(";") : [];
});

// 监听modelValue变化处理回填
// watch(
//   () => props.modelValue,
//   newVal => {
//     if (!newVal.length) {
//       fileList.value = [];
//     } else {
//       fileList.value = newVal.map(item => ({
//         name: item.name,
//         url: item.url
//       }));
//     }
//   },
//   { deep: true }
// );

const showImages = (urlAddr,id)=>{
  if(!props.isNotIncome) {
    return getImageUrl(urlAddr)
  }else{
    getUrl(urlAddr,id)
  }
}
// 查看图片路径
const getUrl = async (urlAddr,id) => {
  const response = await fetch(
    `/gw-api/customer/keyAccountIncomeLog/viewKeyAccountCredential/${urlAddr}`,
    {
      headers: {
        Tenant: getTenant(),
        "Content-Type": "application/json",
        Authorization: getToken()
      }
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  const data = await response.blob();
  const url = URL.createObjectURL(data);
  const previewQrCodeNode = document.getElementById(id)
  previewQrCodeNode.src = url
};

// 阻止自动上传
const beforeUpload = file => {
  const isLtMaxSize = file.size / 1024 / 1024 < props.maxSize;
  if (!isLtMaxSize) {
    ElMessage.error(`文件大小不能超过${props.maxSize}MB`);
    return false;
  }
  return true;
};

// 文件超出限制
const handleExceed = () => {
  ElMessage.error(`最多只能上传${props.limit}个文件`);
};

// 清除文件
const handleClear = index => {
  const newModelValue = previewUrl.value.filter((_, i) => i !== index);
  emit("update:modelValue", newModelValue.join(';'));
  emit("change", newModelValue);
};

// 替换文件
const handleReplace = (item, index) => {
  handleClear(index);
  uploadRef.value.$el.querySelector("input").click();
};

// 移除文件
const handleRemove = (file, fileList) => {
  const newModelValue = previewUrl.value.filter(item => item !== file);
  emit("update:modelValue", newModelValue.join(';'));
  emit("change", newModelValue);
};

const handleSuccess = (response, file) => {
  if (response.code === 200) {
    const fileUrl = props.fullImgUrl
      ? window.origin + "/oss/" + response.data
      : response.data;
    const newModelValue = !props.modelValue
      ? fileUrl
      : props.modelValue + `;${fileUrl}`;
    emit("update:modelValue", newModelValue);
    emit("change", newModelValue);
    ElMessage.success("文件导入成功");
  } else {
    ElMessage.error("文件导入失败");
  }
};

const handleError = err => {
  console.error("上传失败:", err);
  ElMessage.error("上传失败");
};

// 下载：收益凭据(isNotIncome)需带鉴权头取 blob；其余直链下载
const handleDownload = async item => {
  try {
    if (props.isNotIncome) {
      const resp = await fetch(
        `/gw-api/customer/keyAccountIncomeLog/viewKeyAccountCredential/${item}`,
        {
          headers: {
            Tenant: getTenant(),
            "Content-Type": "application/json",
            Authorization: getToken()
          }
        }
      );
      if (!resp.ok) throw new Error("下载失败");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const lastSeg = String(item).split("/").pop() || "credential";
      const filename = /\.[a-zA-Z0-9]+$/.test(lastSeg) ? lastSeg : `${lastSeg}.jpg`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const a = document.createElement("a");
      a.href = getImageUrl(item);
      a.download = "";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch (e) {
    ElMessage.error("下载失败");
  }
};
</script>

<style scoped>
.base64-upload {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}

.hide-upload :deep(.el-upload) {
  display: none;
}

.preview-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
}
.preview-container:hover .preview-actions {
  display: flex;
}

.preview-content {
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  width: v-bind(imageWidth);
  height: v-bind(imageHeight);
  display: block;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
}

.file-name {
  margin-top: 5px;
  font-size: 14px;
  word-break: break-all;
  text-align: center;
}

.file-size {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.preview-actions {
  display: none;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}
.el-icon {
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}
.el-icon:first-child {
  margin-right: 8px;
}
.el-icon.avatar-uploader-icon {
  border: 1px dashed #ddd;
  font-size: 28px;
  color: #8c939d;
  width: 80px;
  height: 80px;
  text-align: center;
}
</style>